<script>
import { required } from "vuelidate/lib/validators";
import validationMessages from "@/components/validations";
import Swal from "sweetalert2";
import General from "@/services/General";
import Customers from "@/services/Customers";
import Notification from "@/services/Notification";

/**
 * Create Push Notification Automation — Figma sections: type, basic info,
 * notification content, app target, audience, schedule, preview, save.
 */
export default {
  name: "ModalAddPushNotification",
  components: {
    validationMessages,
  },
  validations: {
    notificationTitle: {
      required,
    },
    notificationMessage: {
      required,
    },
  },
  data() {
    const notificationFlowTypes = [
      {
        value: "push",
        label: "Push Notification",
        helper: "Standard notification with title and message.",
      },
    ];
    return {
      showModal: false,
      tryingToSubmit: false,
      submitted: false,
      automationName: "",
      automationDescription: "",
      notificationFlowTypes,
      /** Single-select value; add more `notificationFlowTypes` when needed. */
      selectedFlowType: notificationFlowTypes[0].value,
      /** Regular (text-only) vs Rich (image + text). */
      notificationFormat: "regular",
      /** Only used when `notificationFormat === 'rich'`. */
      richImageFile: null,
      richImagePreviewUrl: "",
      /** Push copy (shown in preview + future API). */
      notificationTitle: "",
      notificationMessage: "",
      /** Deeplink `method` from `/general/deeplink_campaign_methods`; empty = home. */
      targetSection: "",
      appSectionMethods: [],
      /** From `GET /general/deeplink_second_level_options/{second_level}` — e.g. video_id → videos list. */
      secondLevelOptions: [],
      /** Selected option `id` (e.g. YouTube id) for `deep_link[second_level_key]`. */
      selectedSecondLevel: "",
      /** Copied from selected method’s `second_level` (e.g. `video_id`) for API path + payload. */
      secondLevelKey: "",
      /** `/customers/groups` — active groups shown in select; static groups also searchable below. */
      systemGroups: [],
      userCreatedGroups: [],
      selectedSystemGroupId: "",
      selectedUserGroup: null,
      userGroupSearch: "",
      estimatedAudienceCount: null,
      estimatedAudienceLoading: false,
      estimatedAudienceRequestId: 0,
      recipient: "ALL",
      /** Schedule: `recurring` | `custom` | `event` — UI variants per design. */
      scheduleType: "immediately",
      scheduleRepeatInterval: "year",
      scheduleTimeRecurring: "10:00",
      scheduleRecurringDayOfWeek: "",
      scheduleRecurringDayOfMonth: "",
      scheduleRecurringDateOfYear: "",
      scheduleCustomDate: "",
      scheduleTimeCustom: "10:00",
      /** Priority processing for push creation API. */
      priority: "low",
      scheduleEventType: "match",
      scheduleEventTiming: "",
      scheduleTimeOffset: "0",
      scheduleTimeOffsetUnit: "hours",
      scheduleApplyToEvents: "",
      clubAppIconIosLoadFailed: false,
      clubAppIconAndroidLoadFailed: false,
    };
  },
  created() {
    this.getDeeplinkCampaignMethods();
    this.getCustomersGroups();
  },
  computed: {
    selectedFlowTypeMeta() {
      return (
        this.notificationFlowTypes.find((t) => t.value === this.selectedFlowType) ||
        this.notificationFlowTypes[0]
      );
    },
    clubAppLabel() {
      const t = process.env.VUE_APP_TENANT || "";
      return t ? `${t} App` : "Your Club App";
    },
    clubInitials() {
      const t = (process.env.VUE_APP_TENANT || "FC").replace(/[^a-zA-Z0-9]/g, "");
      if (t.length >= 2) return t.substring(0, 2).toUpperCase();
      return t ? t.charAt(0).toUpperCase() : "FC";
    },
    clubAppIconTenantSlug() {
      return (process.env.VUE_APP_TENANT || "").trim().toUpperCase();
    },
    clubAppIconCdnBase() {
      return "https://stadiaconnect-cdn.s3.eu-west-1.amazonaws.com/azerion/images/app-icons/";
    },
    clubAppIconIosUrl() {
      if (!this.clubAppIconTenantSlug) {
        return "";
      }
      return `${this.clubAppIconCdnBase}${this.clubAppIconTenantSlug}-IOS.png`;
    },
    clubAppIconAndroidUrl() {
      if (!this.clubAppIconTenantSlug) {
        return "";
      }
      return `${this.clubAppIconCdnBase}${this.clubAppIconTenantSlug}-AND.png`;
    },
    useClubAppIconIosInPreview() {
      return Boolean(this.clubAppIconIosUrl) && !this.clubAppIconIosLoadFailed;
    },
    useClubAppIconAndroidInPreview() {
      return Boolean(this.clubAppIconAndroidUrl) && !this.clubAppIconAndroidLoadFailed;
    },
    previewTitle() {
      const t = (this.notificationTitle || "").trim();
      return t || "Your Notification Title";
    },
    previewMessage() {
      const m = (this.notificationMessage || "").trim();
      return m || "Your notification message will appear here.";
    },
    /** Selected row from deeplink_campaign_methods for the current `targetSection`. */
    selectedTargetMethod() {
      return this.appSectionMethods.find((m) => m.method === this.targetSection) || null;
    },
    /** Show “Specific content” when the API defines a `second_level` for this target. */
    showSecondLevelBlock() {
      const m = this.selectedTargetMethod;
      return !!(m && m.second_level);
    },
    secondLevelFieldLabel() {
      const m = this.selectedTargetMethod;
      if (m && m.second_level_description) {
        return `${m.second_level_description} (Optional)`;
      }
      return "Specific Content (Optional)";
    },
    filteredUserCreatedForSearch() {
      const q = (this.userGroupSearch || "").trim().toLowerCase();
      const list = this.userCreatedGroups;
      if (!q) {
        return list;
      }
      return list.filter((g) => (g.name || "").toLowerCase().includes(q));
    },
    estimatedAudienceDisplay() {
      if (this.estimatedAudienceCount == null || this.estimatedAudienceCount === "") {
        return "—";
      }
      const n = Number(this.estimatedAudienceCount);
      if (Number.isNaN(n)) {
        return "—";
      }
      return n.toLocaleString();
    },
    /** Resolved row for the current system dropdown selection (for summary UI). */
    selectedSystemGroup() {
      if (!this.selectedSystemGroupId) {
        return null;
      }
      return this.systemGroups.find((g) => g.group_id === this.selectedSystemGroupId) || null;
    },
    /** Single label for the shared selection pill (system or user-created). */
    activeAudienceLabel() {
      if (this.selectedSystemGroup) {
        return (this.selectedSystemGroup.name || "").trim();
      }
      if (this.selectedUserGroup) {
        return (this.selectedUserGroup.name || "").trim();
      }
      return "";
    },
    hasActiveAudience() {
      return !!(this.selectedSystemGroupId || this.selectedUserGroup);
    },
    scheduleRepeatIntervalOptions() {
      return [
        { value: "day", label: "Every Day" },
        { value: "week", label: "Every Week" },
        { value: "month", label: "Every Month" },
        { value: "year", label: "Every Year" },
      ];
    },
    scheduleWeekdayOptions() {
      return [
        { value: "monday", label: "Monday" },
        { value: "tuesday", label: "Tuesday" },
        { value: "wednesday", label: "Wednesday" },
        { value: "thursday", label: "Thursday" },
        { value: "friday", label: "Friday" },
        { value: "saturday", label: "Saturday" },
        { value: "sunday", label: "Sunday" },
      ];
    },
    scheduleDayOfMonthOptions() {
      return Array.from({ length: 31 }, (_, i) => {
        const n = i + 1;
        return { value: String(n), label: String(n) };
      });
    },
    scheduleTimeOffsetUnitOptions() {
      return [
        { value: "minutes", label: "Minutes" },
        { value: "hours", label: "Hours" },
        { value: "days", label: "Days" },
      ];
    },
  },
  methods: {
    /** SweetAlert2 sits under Bootstrap-Vue modals by default; bump z-index when opened from this modal. */
    swalAboveModal(options) {
      return Swal.fire({
        ...options,
        didOpen: () => {
          const c = document.querySelector(".swal2-container");
          if (c) {
            c.style.zIndex = "20000";
          }
        },
      });
    },
    formatApiError(error, fallback) {
      const data = error && error.response && error.response.data;
      if (!data) {
        return fallback;
      }
      const err = data.error;
      if (Array.isArray(err)) {
        return err.join("<br>");
      }
      if (err) {
        return String(err);
      }
      if (data.message) {
        return String(data.message);
      }
      return fallback;
    },
    onRichImageSelected(e) {
      const inputEl = e && e.target ? e.target : null;
      const file = inputEl && inputEl.files ? inputEl.files[0] : null;

      if (!file) {
        this.clearRichImage();
        return;
      }

      const maxBytes = 300 * 1024;
      if (file.size > maxBytes) {
        this.clearRichImage();
        if (inputEl) {
          inputEl.value = "";
        }
        this.swalAboveModal({
          icon: "error",
          title: "Image too large",
          html: "Please select an image smaller than 300 KB.",
          confirmButtonColor: "#212529",
        });
        return;
      }

      const tempUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        const ratio = h > 0 ? w / h : 0;
        const targetRatio = 2;
        const tolerance = 0.04;
        const diff = Math.abs(ratio - targetRatio);
        const isInvalid = !w || !h || diff > tolerance;

        if (isInvalid) {
          URL.revokeObjectURL(tempUrl);
          this.clearRichImage();
          if (inputEl) {
            inputEl.value = "";
          }
          this.swalAboveModal({
            icon: "error",
            title: "Invalid aspect ratio",
            html:
              "Please select an image with a 2:1 aspect ratio " +
              "(e.g. 1024x512 px). Selected image is " +
              w +
              "×" +
              h +
              " px (ratio " +
              ratio.toFixed(3) +
              ":1; expected 2:1 ± " +
              tolerance +
              ").",
            confirmButtonColor: "#212529",
          });
          return;
        }

        if (this.richImagePreviewUrl) {
          URL.revokeObjectURL(this.richImagePreviewUrl);
        }
        this.richImageFile = file;
        this.richImagePreviewUrl = tempUrl;
      };
      img.onerror = () => {
        URL.revokeObjectURL(tempUrl);
        this.clearRichImage();
        if (inputEl) {
          inputEl.value = "";
        }
        this.swalAboveModal({
          icon: "error",
          title: "Invalid image",
          html: "The selected file could not be read as an image.",
          confirmButtonColor: "#212529",
        });
      };
      img.src = tempUrl;
    },
    clearRichImage() {
      this.richImageFile = null;
      if (this.richImagePreviewUrl) {
        URL.revokeObjectURL(this.richImagePreviewUrl);
      }
      this.richImagePreviewUrl = "";
    },
    onNotificationFormatChange() {
      if (this.notificationFormat !== "rich") {
        this.clearRichImage();
      }
    },
    onScheduleTypeChange() {
      // Keep values but clear incompatible fields for a clean payload.
      if (this.scheduleType !== "recurring") {
        this.scheduleRecurringDayOfWeek = "";
        this.scheduleRecurringDayOfMonth = "";
      }
      if (this.scheduleType === "event") {
        this.scheduleEventType = "match";
      }
    },
    onScheduleRepeatIntervalChange() {
      // Clear fields that are not applicable to the selected recurring cadence.
      if (this.scheduleRepeatInterval !== "week") {
        this.scheduleRecurringDayOfWeek = "";
      }
      if (this.scheduleRepeatInterval !== "month") {
        this.scheduleRecurringDayOfMonth = "";
      }
      if (this.scheduleRepeatInterval !== "year") {
        this.scheduleRecurringDateOfYear = "";
      }
    },
    onModalShown() {
      this.clubAppIconIosLoadFailed = false;
      this.clubAppIconAndroidLoadFailed = false;
      // Recipient defaults to ALL, so fetch immediately on open.
      this.fetchEstimatedAudience();
    },
    onClubAppIconIosError() {
      this.clubAppIconIosLoadFailed = true;
    },
    onClubAppIconAndroidError() {
      this.clubAppIconAndroidLoadFailed = true;
    },
    /**
     * Fetches estimated audience total from API:  */

    async fetchEstimatedAudience() {
      const recipient = this.recipient || "ALL";
      if (!recipient) {
        this.estimatedAudienceCount = null;
        return;
      }

      // For GROUP recipient, we must wait until a concrete group is selected.
      if (recipient === "GROUP") {
        const group_id =
          this.selectedSystemGroupId ||
          (this.selectedUserGroup && this.selectedUserGroup.group_id) ||
          "";
        if (!group_id) {
          this.estimatedAudienceLoading = false;
          this.estimatedAudienceCount = null;
          return;
        }
      }

      const requestId = ++this.estimatedAudienceRequestId;
      this.estimatedAudienceLoading = true;

      let filters = "";
      if (recipient === "GROUP") {
        const group_id =
          this.selectedSystemGroupId ||
          (this.selectedUserGroup && this.selectedUserGroup.group_id) ||
          "";
        filters = `&group_id=${encodeURIComponent(group_id)}`;
      }

      try {
        const response = await Notification.getAudience(recipient, filters);
        const total =
          (response && response.data && typeof response.data.total !== "undefined"
            ? response.data.total
            : null);
        if (requestId === this.estimatedAudienceRequestId) {
          this.estimatedAudienceCount = total != null ? Number(total) : null;
        }
      } catch (e) {
        if (requestId !== this.estimatedAudienceRequestId) {
          return;
        }
        // Fallback to local totals for GROUP when available
        if (recipient === "GROUP") {
          if (this.selectedSystemGroupId) {
            const g = this.systemGroups.find((x) => x.group_id === this.selectedSystemGroupId);
            this.estimatedAudienceCount = g && g.total_members != null ? g.total_members : null;
            return;
          }
          if (this.selectedUserGroup && this.selectedUserGroup.total_members != null) {
            this.estimatedAudienceCount = this.selectedUserGroup.total_members;
            return;
          }
        }
        this.estimatedAudienceCount = null;
      } finally {
        if (requestId === this.estimatedAudienceRequestId) {
          this.estimatedAudienceLoading = false;
        }
      }
    },
    getCustomersGroups() {
      Customers.getCustomersGroups()
        .then((response) => {
          const rows = response.data.data ? response.data.data : [];
          this.systemGroups = rows.filter((g) => Number(g.active) === 1);
          this.userCreatedGroups = rows.filter((g) => g.type === "static" && Number(g.active) === 1);
        })
        .catch(() => {
          this.systemGroups = [];
          this.userCreatedGroups = [];
        });
    },
    onSystemGroupChange() {
      if (this.selectedSystemGroupId) {
        this.selectedUserGroup = null;
        this.userGroupSearch = "";
      }
      this.fetchEstimatedAudience();
    },
    clearSystemGroupSelection() {
      this.selectedSystemGroupId = "";
      this.fetchEstimatedAudience();
    },
    pickUserCreatedGroup(group) {
      this.selectedUserGroup = { ...group };
      this.selectedSystemGroupId = "";
      this.userGroupSearch = "";
      this.fetchEstimatedAudience();
    },
    clearUserCreatedSelection() {
      this.selectedUserGroup = null;
      this.userGroupSearch = "";
      this.fetchEstimatedAudience();
    },
    /** Clears whichever audience source is active (matches single pill in UI). */
    clearActiveAudience() {
      if (this.selectedSystemGroupId) {
        this.clearSystemGroupSelection();
      } else if (this.selectedUserGroup) {
        this.clearUserCreatedSelection();
      }
    },
    applyEstimatedFromSelection() {
      // Backwards compatible alias (older UI calls)
      this.fetchEstimatedAudience();
    },
    refreshEstimatedAudience() {
      this.fetchEstimatedAudience();
    },
    onRecipientChange() {
      if (this.recipient !== "GROUP") {
        this.selectedSystemGroupId = "";
        this.selectedUserGroup = null;
        this.userGroupSearch = "";
      }
      this.fetchEstimatedAudience();
    },
    getDeeplinkCampaignMethods() {
      General.getDeeplinkCampaignMethods()
        .then((response) => {
          this.appSectionMethods = response.data.data ? response.data.data : [];
        })
        .catch(() => {
          this.appSectionMethods = [];
        });
    },
    /**
     * Loads options for the second select from
     * `GET /general/deeplink_second_level_options/{second_level}` (e.g. `video_id`).
     */
    loadSecondLevelOptions(secondLevelSlug) {
      if (!secondLevelSlug) {
        this.secondLevelOptions = [];
        this.selectedSecondLevel = "";
        this.secondLevelKey = "";
        return;
      }
      this.secondLevelKey = secondLevelSlug;
      General.getDeeplinkSecondLevelOptions(secondLevelSlug)
        .then((response) => {
          this.secondLevelOptions = response.data.data ? response.data.data : [];
          this.selectedSecondLevel = "";
        })
        .catch(() => {
          this.secondLevelOptions = [];
          this.selectedSecondLevel = "";
        });
    },
    onTargetSectionChange() {
      this.selectedSecondLevel = "";
      this.secondLevelOptions = [];
      this.secondLevelKey = "";
      const m = this.selectedTargetMethod;
      if (m && m.second_level) {
        this.loadSecondLevelOptions(m.second_level);
      }
    },
    refreshNotificatinData() {
      this.$emit("refreshScreen");
    },
    goBack() {
      this.closeModal();
    },
    closeModal() {
      this.showModal = false;
      this.tryingToSubmit = false;
      this.submitted = false;
      this.automationName = "";
      this.automationDescription = "";
      this.selectedFlowType = this.notificationFlowTypes[0].value;
      this.notificationFormat = "regular";
      this.clearRichImage();
      this.notificationTitle = "";
      this.notificationMessage = "";
      this.targetSection = "";
      this.secondLevelOptions = [];
      this.selectedSecondLevel = "";
      this.secondLevelKey = "";
      this.selectedSystemGroupId = "";
      this.selectedUserGroup = null;
      this.userGroupSearch = "";
      this.estimatedAudienceCount = null;
      this.estimatedAudienceLoading = false;
      this.estimatedAudienceRequestId = 0;
      this.recipient = "ALL";
      this.scheduleType = "immediately";
      this.scheduleRepeatInterval = "year";
      this.scheduleTimeRecurring = "10:00";
      this.scheduleRecurringDayOfWeek = "";
      this.scheduleRecurringDayOfMonth = "";
      this.scheduleRecurringDateOfYear = "";
      this.scheduleCustomDate = "";
      this.scheduleTimeCustom = "10:00";
      this.scheduleEventType = "match";
      this.scheduleEventTiming = "";
      this.scheduleTimeOffset = "0";
      this.scheduleTimeOffsetUnit = "hours";
      this.scheduleApplyToEvents = "";
      this.priority = "low";
    },
    resetProps() {
      this.closeModal();
    },
    buildNotificationCreateParams() {
      const csrf_token = localStorage.getItem("csrf_token");
      const resolvedPriority = this.priority || "low";
      const params = {
        title: (this.notificationTitle || "").trim(),
        message: (this.notificationMessage || "").trim(),
        csrf_token,
        recipient: this.recipient || "ALL",
        priority: resolvedPriority,
        processing_range: resolvedPriority === "high" ? "30" : "60",
        deep_link: {
          action: this.targetSection || "",
          fullscreen: "Yes",
        },
      };

      if (this.secondLevelKey && this.selectedSecondLevel) {
        params.deep_link[this.secondLevelKey] = this.selectedSecondLevel;
      }

      if ((this.recipient || "ALL") === "GROUP") {
        const group_id =
          this.selectedSystemGroupId ||
          (this.selectedUserGroup && this.selectedUserGroup.group_id) ||
          "";

        if (group_id) {
          params.group_id = group_id;
          params.group_type = this.selectedSystemGroup
            ? this.selectedSystemGroup.type || ""
            : this.selectedUserGroup
              ? this.selectedUserGroup.type || "static"
              : "";
        }
      }

      if (this.scheduleType === "custom" && this.scheduleCustomDate && this.scheduleTimeCustom) {
        params.schedule_time = `${this.scheduleCustomDate} ${this.scheduleTimeCustom}:00`;
      } else {
        params.schedule_time = "";
      }

      return params;
    },
    buildNotificationCreateFormData(params) {
      const fd = new FormData();

      // top-level scalar fields
      Object.keys(params || {}).forEach((k) => {
        if (k === "deep_link") return;
        if (k === "csrf_token") return; // CSRF header is already set by Http interceptor
        const v = params[k];
        if (typeof v === "undefined" || v === null) return;
        fd.append(k, String(v));
      });

      // deep_link[...] fields
      const dl = (params && params.deep_link) || {};
      Object.keys(dl).forEach((k) => {
        const v = dl[k];
        if (typeof v === "undefined" || v === null) return;
        fd.append(`deep_link[${k}]`, String(v));
      });

      // image (Rich)
      if (this.notificationFormat === "rich" && this.richImageFile) {
        fd.append("notification_image", this.richImageFile);
      }

      return fd;
    },
    buildAutomatedScheduleFormData(payload) {
      const fd = new FormData();

      // top-level scalar fields
      Object.keys(payload || {}).forEach((k) => {
        if (k === "attributes") return;
        const v = payload[k];
        if (typeof v === "undefined" || v === null) return;
        fd.append(k, String(v));
      });

      const attrs = (payload && payload.attributes) || {};
      Object.keys(attrs).forEach((k) => {
        if (k === "deep_link") return;
        const v = attrs[k];
        if (typeof v === "undefined" || v === null) return;
        fd.append(`attributes[${k}]`, String(v));
      });

      const dl = (attrs && attrs.deep_link) || {};
      Object.keys(dl).forEach((k) => {
        const v = dl[k];
        if (typeof v === "undefined" || v === null) return;
        fd.append(`attributes[deep_link][${k}]`, String(v));
      });

      if (this.notificationFormat === "rich" && this.richImageFile) {
        fd.append("notification_image", this.richImageFile);
      }

      return fd;
    },
    parseTimeToCronParts(hhmm) {
      const t = (hhmm || "").trim();
      const m = t.match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return null;
      const hour = Number(m[1]);
      const minute = Number(m[2]);
      if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
      if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
      return { hour, minute };
    },
    weekdayToCronValue(day) {
      // Backend expects 0-6 (SUN-SAT) based on PHP DateTime->format('w')
      const map = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
      };
      return typeof map[day] !== "undefined" ? map[day] : null;
    },
    buildExecuteAtCron() {
      const interval = this.scheduleRepeatInterval;
      const time = this.parseTimeToCronParts(this.scheduleTimeRecurring);
      if (!time) return null;
      const { hour, minute } = time;

      if (interval === "day") {
        return `${minute} ${hour} * * *`;
      }
      if (interval === "week") {
        const wd = (this.scheduleRecurringDayOfWeek || "").trim();
        const cronWd = this.weekdayToCronValue(wd);
        if (cronWd == null) return null;
        return `${minute} ${hour} * * ${cronWd}`;
      }
      if (interval === "month") {
        const dom = Number(this.scheduleRecurringDayOfMonth);
        if (!dom || dom < 1 || dom > 31) return null;
        return `${minute} ${hour} ${dom} * *`;
      }
      if (interval === "year") {
        const d = (this.scheduleRecurringDateOfYear || "").trim(); // YYYY-MM-DD
        const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!m) return null;
        const month = Number(m[2]);
        const day = Number(m[3]);
        if (!month || month < 1 || month > 12 || !day || day < 1 || day > 31) return null;
        return `${minute} ${hour} ${day} ${month} *`;
      }

      // Fallback (should not happen)
      return null;
    },
    buildAutomatedScheduleParams() {
      const resolvedPriority = (this.priority || "low").trim() || "low";
      const processingRange = resolvedPriority === "high" ? "30" : "60";

      const deepLinkAction = (this.targetSection || "").trim();
      const deepLink = deepLinkAction
        ? {
            action: deepLinkAction,
            fullscreen: "Yes",
          }
        : null;

      if (deepLink && this.secondLevelKey && this.selectedSecondLevel) {
        deepLink[this.secondLevelKey] = this.selectedSecondLevel;
      }

      const pad2 = (n) => String(n).padStart(2, "0");
      // Backend expects "YYYY-MM-DD HH:MM:SS"; we always send seconds as "00"
      // because scheduling is minute-based (cron minute/hour fields).
      const formatDateTime = (d) =>
        `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
        `${pad2(d.getHours())}:${pad2(d.getMinutes())}:00`;
      const startOfMinute = (d) =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), 0);
      const daysInMonth = (year, month1to12) => new Date(year, month1to12, 0).getDate();

      const buildNextScheduleTime = () => {
        const interval = this.scheduleRepeatInterval;
        const time = this.parseTimeToCronParts(this.scheduleTimeRecurring);
        if (!time) return "";

        const now = new Date();
        const base = startOfMinute(now);

        if (interval === "day") {
          const candidate = new Date(
            base.getFullYear(),
            base.getMonth(),
            base.getDate(),
            time.hour,
            time.minute,
            0
          );
          if (candidate.getTime() <= base.getTime()) {
            candidate.setDate(candidate.getDate() + 1);
          }
          return formatDateTime(candidate);
        }

        if (interval === "week") {
          const wd = (this.scheduleRecurringDayOfWeek || "").trim();
          const cronWd = this.weekdayToCronValue(wd);
          if (cronWd == null) return "";

          const todayWd = base.getDay(); // 0-6 (Sun-Sat)
          let diff = (cronWd - todayWd + 7) % 7;
          const candidate = new Date(
            base.getFullYear(),
            base.getMonth(),
            base.getDate(),
            time.hour,
            time.minute,
            0
          );
          candidate.setDate(candidate.getDate() + diff);
          if (candidate.getTime() <= base.getTime()) {
            candidate.setDate(candidate.getDate() + 7);
          }
          return formatDateTime(candidate);
        }

        if (interval === "month") {
          const dom = Number(this.scheduleRecurringDayOfMonth);
          if (!dom || dom < 1 || dom > 31) return "";

          let y = base.getFullYear();
          let m = base.getMonth() + 1; // 1-12

          // Find next month where the requested day exists and time is in the future.
          for (let i = 0; i < 24; i++) {
            const dim = daysInMonth(y, m);
            if (dom <= dim) {
              const candidate = new Date(y, m - 1, dom, time.hour, time.minute, 0);
              if (candidate.getTime() > base.getTime()) {
                return formatDateTime(candidate);
              }
            }
            m += 1;
            if (m > 12) {
              m = 1;
              y += 1;
            }
          }
          return "";
        }

        if (interval === "year") {
          const d = (this.scheduleRecurringDateOfYear || "").trim(); // YYYY-MM-DD
          const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (!m) return "";
          const month = Number(m[2]);
          const day = Number(m[3]);
          if (!month || month < 1 || month > 12 || !day || day < 1 || day > 31) return "";

          const baseYear = base.getFullYear();
          for (let y = baseYear; y <= baseYear + 3; y++) {
            const dim = daysInMonth(y, month);
            if (day > dim) {
              continue; // invalid date for this year (e.g., Feb 29)
            }
            const candidate = new Date(y, month - 1, day, time.hour, time.minute, 0);
            if (candidate.getTime() > base.getTime()) {
              return formatDateTime(candidate);
            }
          }
          return "";
        }

        return "";
      };

      const scheduleTime = buildNextScheduleTime();

      const params = {
        type: "promotion",
        execute_at: this.buildExecuteAtCron(),
        execute_at_query: null,
        fixture_related: 0,
        name: (this.notificationTitle || "").trim(),
        recipient: (this.recipient || "ALL").toUpperCase(),
        active: 1,
        query: null,
        attributes: {
          title: (this.notificationTitle || "").trim(),
          message: (this.notificationMessage || "").trim(),
          process_immediately: 0,
          message_type: "promotions",
          ...(deepLink ? { deep_link: deepLink } : {}),
          priority: resolvedPriority,
          processing_range: processingRange,
          schedule_time: scheduleTime,
          notification_type: "promotions",
        },
        created_by: "dashboard",
      };

      if (params.recipient === "GROUP") {
        const group_id =
          this.selectedSystemGroupId ||
          (this.selectedUserGroup && this.selectedUserGroup.group_id) ||
          "";
        if (group_id) {
          params.group_id = group_id;
        }
      }

      return params;
    },
    buildAutomatedScheduleEventParams() {
      const resolvedPriority = (this.priority || "low").trim() || "low";
      const processingRange = resolvedPriority === "high" ? "30" : "60";

      const deepLinkAction = (this.targetSection || "").trim();
      const deepLink = deepLinkAction
        ? {
            action: deepLinkAction,
            fullscreen: "Yes",
          }
        : null;

      if (deepLink && this.secondLevelKey && this.selectedSecondLevel) {
        deepLink[this.secondLevelKey] = this.selectedSecondLevel;
      }

      const params = {
        type: "promotion",
        schedule_type: "event",
        event_timing: (this.scheduleEventTiming || "").trim(),
        time_offset: (this.scheduleTimeOffset || "0").trim(),
        time_offset_unit: this.scheduleTimeOffsetUnit || "hours",
        apply_to_events: this.scheduleApplyToEvents || "all",
        execute_at: null,
        execute_at_query: null,
        fixture_related: 1,
        name: (this.notificationTitle || "").trim(),
        recipient: (this.recipient || "ALL").toUpperCase(),
        active: 1,
        query: null,
        attributes: {
          title: (this.notificationTitle || "").trim(),
          message: (this.notificationMessage || "").trim(),
          process_immediately: 0,
          message_type: "promotions",
          ...(deepLink ? { deep_link: deepLink } : {}),
          priority: resolvedPriority,
          processing_range: processingRange,
          notification_type: "promotions",
        },
        created_by: "dashboard",
      };

      if (params.recipient === "GROUP") {
        const group_id =
          this.selectedSystemGroupId ||
          (this.selectedUserGroup && this.selectedUserGroup.group_id) ||
          "";
        if (group_id) {
          params.group_id = group_id;
        }
      }

      return params;
    },
    runSave(status) {
      this.submitted = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.tryingToSubmit = true;

      if (status === "active") {
        if (this.scheduleType === "event") {
          if (!this.scheduleEventTiming) {
            this.swalAboveModal({
              icon: "error",
              title: "Missing event timing",
              html: "Please select when the notification should be sent relative to the event.",
              confirmButtonColor: "#212529",
            }).finally(() => {
              this.tryingToSubmit = false;
            });
            return;
          }
          if (!this.scheduleApplyToEvents) {
            this.swalAboveModal({
              icon: "error",
              title: "Missing event scope",
              html: "Please select which matches this automation applies to.",
              confirmButtonColor: "#212529",
            }).finally(() => {
              this.tryingToSubmit = false;
            });
            return;
          }
          const scheduleParams = this.buildAutomatedScheduleEventParams();
          if (scheduleParams.recipient === "GROUP" && !scheduleParams.group_id) {
            this.swalAboveModal({
              icon: "error",
              title: "Missing group",
              html: "Please select a group for recipient GROUP.",
              confirmButtonColor: "#212529",
            }).finally(() => {
              this.tryingToSubmit = false;
            });
            return;
          }

          const schedulePayload =
            this.notificationFormat === "rich" && this.richImageFile
              ? this.buildAutomatedScheduleFormData(scheduleParams)
              : scheduleParams;

          Notification.createAutomatedSchedule(schedulePayload)
            .then((response) => {
              const msg =
                (response && response.data && (response.data.message || response.data.success)) ||
                "Event schedule saved successfully";
              Swal.fire({
                icon: "success",
                title: "Scheduled",
                html: msg,
                confirmButtonColor: "#212529",
              });
              this.refreshNotificatinData();
              this.closeModal();
            })
            .catch((error) => {
              const err = this.formatApiError(
                error,
                "Event schedule could not be created"
              );
              Swal.fire({
                icon: "error",
                title: "Failed",
                html: err,
                confirmButtonColor: "#212529",
              });
            })
            .finally(() => {
              this.tryingToSubmit = false;
            });
          return;
        }

        if (this.scheduleType === "recurring") {
          const scheduleParams = this.buildAutomatedScheduleParams();
          if (!scheduleParams.execute_at) {
            Swal.fire({
              icon: "error",
              title: "Missing schedule details",
              html: "Please complete the recurring schedule details (interval/day/date/time).",
              confirmButtonColor: "#212529",
            }).finally(() => {
              this.tryingToSubmit = false;
            });
            return;
          }
          if (scheduleParams.recipient === "GROUP" && !scheduleParams.group_id) {
            Swal.fire({
              icon: "error",
              title: "Missing group",
              html: "Please select a group for recipient GROUP.",
              confirmButtonColor: "#212529",
            }).finally(() => {
              this.tryingToSubmit = false;
            });
            return;
          }

          const schedulePayload =
            this.notificationFormat === "rich" && this.richImageFile
              ? this.buildAutomatedScheduleFormData(scheduleParams)
              : scheduleParams;

          Notification.createAutomatedSchedule(schedulePayload)
            .then((response) => {
              const msg =
                (response && response.data && (response.data.message || response.data.success)) ||
                "Schedule saved successfully";
              Swal.fire({
                icon: "success",
                title: "Scheduled",
                html: msg,
                confirmButtonColor: "#212529",
              });
              this.refreshNotificatinData();
              this.closeModal();
            })
            .catch((error) => {
              const err =
                (error &&
                  error.response &&
                  error.response.data &&
                  (error.response.data.error || error.response.data.message)) ||
                "Schedule could not be created";
              Swal.fire({
                icon: "error",
                title: "Failed",
                html: err,
                confirmButtonColor: "#212529",
              });
            })
            .finally(() => {
              this.tryingToSubmit = false;
            });
          return;
        }

        const params = this.buildNotificationCreateParams();
        const createPayload =
          this.notificationFormat === "rich" && this.richImageFile
            ? this.buildNotificationCreateFormData(params)
            : params;

        Notification.create(createPayload)
          .then((response) => {
            const msg =
              (response && response.data && (response.data.message || response.data.success)) ||
              "Notification queued successfully";
            Swal.fire({
              icon: "success",
              title: "Notification Created",
              html: '',
              confirmButtonColor: "#212529",
            });
            this.refreshNotificatinData();
            this.closeModal();
          })
          .catch((error) => {
            const err =
              (error &&
                error.response &&
                error.response.data &&
                (error.response.data.error || error.response.data.message)) ||
              "Notification could not be created";
            Swal.fire({
              icon: "error",
              title: "Failed",
              html: err,
              confirmButtonColor: "#212529",
            });
          })
          .finally(() => {
            this.tryingToSubmit = false;
          });
        return;
      }

      const payload = {
        status,
        name: this.automationName.trim(),
        description: (this.automationDescription || "").trim(),
        flowType: this.selectedFlowType,
        notificationTitle: (this.notificationTitle || "").trim(),
        notificationMessage: (this.notificationMessage || "").trim(),
        targetSection: this.targetSection || "",
        secondLevelKey: this.secondLevelKey || "",
        selectedSecondLevel: this.selectedSecondLevel || "",
        audienceSource: this.selectedSystemGroupId
          ? "system"
          : this.selectedUserGroup
            ? "user_created"
            : "",
        groupId:
          this.selectedSystemGroupId ||
          (this.selectedUserGroup && this.selectedUserGroup.group_id) ||
          "",
        schedule: {
          type: this.scheduleType,
          recurring:
            this.scheduleType === "recurring"
              ? {
                  repeatInterval: this.scheduleRepeatInterval,
                  time: this.scheduleTimeRecurring || "",
                  dayOfWeek: this.scheduleRepeatInterval === "week" ? this.scheduleRecurringDayOfWeek || "" : "",
                  dayOfMonth:
                    this.scheduleRepeatInterval === "month" ? this.scheduleRecurringDayOfMonth || "" : "",
                  dateOfYear:
                    this.scheduleRepeatInterval === "year" ? this.scheduleRecurringDateOfYear || "" : "",
                }
              : null,
          custom:
            this.scheduleType === "custom"
              ? {
                  date: this.scheduleCustomDate || "",
                  time: this.scheduleTimeCustom || "",
                }
              : null,
          event:
            this.scheduleType === "event"
              ? {
                  eventType: this.scheduleEventType || "",
                  timing: this.scheduleEventTiming || "",
                  timeOffset: (this.scheduleTimeOffset || "").trim(),
                  timeOffsetUnit: this.scheduleTimeOffsetUnit || "",
                  applyToEvents: this.scheduleApplyToEvents || "",
                }
              : null,
        },
      };
      this.$emit("automation-save", payload);
      Swal.fire({
        icon: "info",
        title: "Draft",
        html: "Draft save is not connected to the API yet. Payload is ready for the next integration step.",
        confirmButtonColor: "#212529",
      }).finally(() => {
        this.tryingToSubmit = false;
        this.closeModal();
      });
    },
    saveAsDraft() {
      this.runSave("draft");
    },
    saveAndActivate() {
      this.runSave("active");
    },
  },
};
</script>

<template>
  <b-modal
    id="add_new_push_notification"
    v-model="showModal"
    hide-header
    hide-footer
    size="xl"
    dialog-class="modal-dialog-push-automation-wide"
    body-class="p-0"
    content-class="border-0 shadow"
    @shown="onModalShown"
    @hidden="resetProps"
  >
    <header class="push-automation-header bg-white border-bottom px-3 px-md-4 py-3">
      <div class="d-flex align-items-start">
        <button
          type="button"
          class="btn btn-link text-decoration-none text-dark ps-0 pt-0 flex-shrink-0 d-flex align-items-center mt-2 font-size-17"
          @click="goBack"
        >
          <i class="uil uil-arrow-left me-1"></i> Back
        </button>
        <div class="ms-3 min-w-0 flex-grow-1">
          <h4 class="fw-semibold mb-0">Create Push Notification Automation</h4>
          <p class="text-muted small mb-0 mt-1">Set up automated notifications for your fans</p>
        </div>
      </div>
    </header>

    <div class="push-automation-body bg-light px-3 px-md-4 py-4">
      <div class="row g-4">
        <div class="col-lg-8">
          <div class="card push-automation-card rounded-3 mb-4">
            <div class="card-body p-4">
              <h6 class="fw-semibold mb-1">Notification Type</h6>
              <p class="text-muted small mb-3">Choose the type of push notification to create.</p>
              <label class="form-label small fw-medium" for="push-flow-type">Type</label>
              <select
                id="push-flow-type"
                v-model="selectedFlowType"
                class="form-select form-control push-automation-field push-flow-type-select"
              >
                <option
                  v-for="opt in notificationFlowTypes"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
              <p class="text-muted small mt-2 mb-0">{{ selectedFlowTypeMeta.helper }}</p>

<!--
              <div class="mt-3">
                <label class="form-label small fw-medium d-block mb-2">Format</label>
                <div class="btn-group btn-group-sm" role="group" aria-label="Notification format">
                  <input
                    id="format-regular"
                    class="btn-check"
                    type="radio"
                    name="notification_format"
                    value="regular"
                    v-model="notificationFormat"
                    @change="onNotificationFormatChange"
                    autocomplete="off"
                  />
                  <label class="btn btn-outline-dark" for="format-regular">Regular</label>

                  <input
                    id="format-rich"
                    class="btn-check"
                    type="radio"
                    name="notification_format"
                    value="rich"
                    v-model="notificationFormat"
                    @change="onNotificationFormatChange"
                    autocomplete="off"
                  />
                  <label class="btn btn-outline-dark" for="format-rich">Rich</label>
                </div>
                <p class="text-muted small mt-2 mb-0">
                  Regular sends title &amp; message. Rich allows adding an image.
                </p>
              </div>

              <div v-if="notificationFormat === 'rich'" class="mt-3">
                <label class="form-label small fw-medium" for="rich-image-import">Import image</label>
                <input
                  id="rich-image-import"
                  type="file"
                  accept="image/*"
                  class="form-control rounded-3 push-automation-field"
                  @change="onRichImageSelected"
                />
                <div v-if="richImagePreviewUrl" class="mt-2 d-flex align-items-center gap-3">
                  <img
                    :src="richImagePreviewUrl"
                    alt="Selected notification image preview"
                    class="rich-image-preview rounded-3 border"
                  />
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-dark"
                    @click="clearRichImage"
                  >
                    Remove
                  </button>
                </div>
                <p class="text-muted small mt-2 mb-0">Supported: JPG/PNG/WebP. Aspect ratio 2:1 (e.g. 1024x512 px). Max 300 KB.</p>
              </div>
-->
            </div>
          </div>

<!--
          <div class="card push-automation-card rounded-3 mb-4">
            <div class="card-body p-4">
              <h6 class="fw-semibold mb-1">Basic Information</h6>
              <p class="text-muted small mb-3">Give your automation a name and description.</p>

              <div class="mb-3">
                <label class="form-label small fw-medium" for="automation-name">Name *</label>
                <input
                  id="automation-name"
                  v-model="automationName"
                  type="text"
                  class="form-control rounded-3 push-automation-field"
                  :class="{ 'is-invalid': submitted && $v.automationName.$error }"
                  placeholder="e.g., Match Day Reminder - 48h Before"
                  autocomplete="off"
                />
                <validationMessages
                  v-if="submitted"
                  field-name="Name"
                  :validation-name="$v.automationName"
                />
              </div>

              <div class="mb-0">
                <label class="form-label small fw-medium" for="automation-desc"
                  >Description (Optional)</label
                >
                <textarea
                  id="automation-desc"
                  v-model="automationDescription"
                  rows="4"
                  class="form-control rounded-3 push-automation-field"
                  placeholder="Describe what this automation does..."
                ></textarea>
              </div>
            </div>
          </div>
 -->

          <div class="card push-automation-card rounded-3 mb-4">
            <div class="card-body p-4">
              <h6 class="fw-semibold mb-1">Notification Content</h6>
              <p class="text-muted small mb-3">Craft your notification message</p>

              <div class="mb-3">
                <label class="form-label small fw-medium" for="notification-content-title">Title *</label>
                <input
                  id="notification-content-title"
                  v-model="notificationTitle"
                  type="text"
                  maxlength="50"
                  class="form-control rounded-3 push-automation-field"
                  :class="{ 'is-invalid': submitted && $v.notificationTitle.$error }"
                  placeholder="e.g., Match Starting Soon! ⚽"
                  autocomplete="off"
                />
                <p class="text-muted small mt-1 mb-0">Max 50 characters</p>
                <validationMessages
                  v-if="submitted"
                  field-name="Title"
                  :validation-name="$v.notificationTitle"
                />
              </div>

              <div class="mb-0">
                <label class="form-label small fw-medium" for="notification-content-message">Message *</label>
                <textarea
                  id="notification-content-message"
                  v-model="notificationMessage"
                  rows="4"
                  maxlength="150"
                  class="form-control rounded-3 push-automation-field"
                  :class="{ 'is-invalid': submitted && $v.notificationMessage.$error }"
                  placeholder="e.g., Don't miss the kickoff in 2 hours! Predict the score now and win prizes."
                ></textarea>
                <p class="text-muted small mt-1 mb-0">Max 150 characters</p>
                <validationMessages
                  v-if="submitted"
                  field-name="Message"
                  :validation-name="$v.notificationMessage"
                />
              </div>
            </div>
          </div>

          <div class="card push-automation-card rounded-3 mb-4">
            <div class="card-body p-4">
              <h6 class="fw-semibold mb-1">App Section</h6>
              <p class="text-muted small mb-3">
                Select which section of the app this notification should link to
              </p>
              <label class="form-label small fw-medium" for="target-app-section">Target Section</label>
              <select
                id="target-app-section"
                v-model="targetSection"
                class="form-select form-control push-automation-field push-flow-type-select"
                @change="onTargetSectionChange"
              >
                <option value="" disabled>Select app section</option>
                <option
                  v-for="item in appSectionMethods"
                  :key="item.method"
                  :value="item.method"
                >
                  {{ item.description }}
                </option>
              </select>

              <div v-if="showSecondLevelBlock" class="mt-3 mb-0">
                <label class="form-label small fw-medium" for="target-specific-content">{{
                  secondLevelFieldLabel
                }}</label>
                <select
                  id="target-specific-content"
                  v-model="selectedSecondLevel"
                  class="form-select form-control push-automation-field push-flow-type-select"
                  :disabled="secondLevelOptions.length === 0"
                >
                  <option value="" disabled>Select specific content</option>
                  <option
                    v-for="opt in secondLevelOptions"
                    :key="opt.id"
                    :value="opt.id"
                  >
                    {{ opt.name }}
                  </option>
                </select>
                <p class="text-muted small mt-1 mb-0">
                  Link to specific content within this section
                </p>
              </div>
            </div>
          </div>

          <div class="card push-automation-card rounded-3 mb-4">
            <div class="card-body p-4">
              <h6 class="fw-semibold mb-1">Audience Targeting</h6>
              <p class="text-muted small mb-3">Select one audience group for this notification</p>

              <div class="mb-3">
                <label class="form-label small fw-medium mb-2">Recipients</label>
                <select
                  type="select"
                  class="form-control form-select push-automation-field push-flow-type-select"
                  v-model="recipient"
                  @change="onRecipientChange"
                >
                  <option value="ALL">All Users</option>
                  <option value="IOS">IOS Users</option>
                  <option value="ANDROID">Android Users</option>
                  <option value="GROUP">Group</option>
                </select>
              </div>

              <div v-if="recipient === 'GROUP'">
                <label class="form-label small fw-medium mb-2">Audience Group</label>

                <p class="text-muted text-uppercase small fw-semibold mb-2 audience-sub-label">System groups</p>
                <select
                  id="audience-system-group"
                  v-model="selectedSystemGroupId"
                  class="form-select form-control push-automation-field push-flow-type-select audience-system-select mb-3"
                  :disabled="!!selectedUserGroup"
                  @change="onSystemGroupChange"
                >
                  <option value="">Select a system group</option>
                  <option
                    v-for="g in systemGroups"
                    :key="g.group_id"
                    :value="g.group_id"
                  >
                    {{ g.name }}
                  </option>
                </select>

                <div class="audience-or-divider" aria-hidden="true">
                  <span></span>
                  <strong>OR</strong>
                  <span></span>
                </div>

                <p class="text-muted text-uppercase small fw-semibold mb-2 audience-sub-label">
                  User created groups
                </p>
                <div class="audience-user-block">
                  <div
                    class="audience-user-search-wrap"
                    :class="{ 'audience-user-search-wrap--muted': !!selectedSystemGroupId }"
                  >
                    <div class="position-relative">
                      <i
                        class="uil uil-search position-absolute text-muted audience-search-icon"
                        aria-hidden="true"
                      ></i>
                      <input
                        v-model="userGroupSearch"
                        type="text"
                        class="form-control rounded-3 push-automation-field ps-5 audience-user-search-input"
                        placeholder="Search user created groups..."
                        autocomplete="off"
                        :disabled="!!selectedSystemGroupId"
                      />
                      <ul
                        v-if="filteredUserCreatedForSearch.length > 0 && userGroupSearch.trim() && !selectedSystemGroupId"
                        class="list-group position-absolute w-100 shadow-sm mt-1 audience-suggest-list"
                      >
                        <li
                          v-for="g in filteredUserCreatedForSearch"
                          :key="g.group_id"
                          class="list-group-item list-group-item-action small py-2 cursor-pointer"
                          role="button"
                          @click="pickUserCreatedGroup(g)"
                        >
                          {{ g.name }}
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div
                    v-if="hasActiveAudience && activeAudienceLabel"
                    class="audience-active-pill d-flex align-items-center justify-content-between gap-2 mt-3"
                  >
                    <span class="d-flex align-items-center min-w-0 flex-grow-1">
                      <span class="audience-active-dot flex-shrink-0" aria-hidden="true"></span>
                      <span class="audience-active-name small fw-semibold text-truncate">{{ activeAudienceLabel }}</span>
                    </span>
                    <button
                      type="button"
                      class="audience-active-clear flex-shrink-0"
                      aria-label="Clear audience group"
                      @click="clearActiveAudience"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
              

              <div class="border rounded-3 p-3 mt-4 audience-estimated-box">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <span class="fw-semibold small">Estimated Audience</span>
                 <!-- <button
                    type="button"
                    class="btn btn-link btn-sm p-0 text-decoration-none text-dark d-flex align-items-center border-1 border-primary rounded-3 p-1"
                    @click="refreshEstimatedAudience"
                  >
                    <i class="uil uil-sync me-1"></i>   Refresh
                  </button> -->
                </div>
                <p class="text-muted extra-small mb-2 audience-estimated-footnote">
                  * Estimates are for users with the app installed
                </p>
                <div class="text-center fs-1 fw-bold py-1 audience-estimated-value">
                  <span v-if="estimatedAudienceLoading" class="d-inline-flex align-items-center justify-content-center gap-2">
                    <b-spinner class="align-middle"></b-spinner>
                  </span>
                  <span v-else>{{ estimatedAudienceDisplay }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="card push-automation-card rounded-3">
            <div class="card-body p-4">
              <h6 class="fw-semibold mb-1">Schedule</h6>
              <p class="text-muted small mb-3">Define when this automation should run</p>

              <div class="mb-3">
                <label class="form-label small fw-medium" for="schedule-type">Schedule Type</label>
                <select
                  id="schedule-type"
                  v-model="scheduleType"
                  class="form-select form-control push-automation-field push-flow-type-select"
                  @change="onScheduleTypeChange"
                >
                  <option value="immediately">Send Immediately</option>
                  <option value="recurring">Recurring Schedule</option>
                  <option value="custom">Custom Date &amp; Time</option>
                  <option value="event">Connect to Event</option>
                </select>
              </div>

              <template v-if="scheduleType === 'recurring'">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label small fw-medium" for="schedule-repeat-interval"
                      >Repeat Interval</label
                    >
                    <select
                      id="schedule-repeat-interval"
                      v-model="scheduleRepeatInterval"
                      class="form-select form-control push-automation-field push-flow-type-select"
                      @change="onScheduleRepeatIntervalChange"
                    >
                      <option
                        v-for="opt in scheduleRepeatIntervalOptions"
                        :key="opt.value"
                        :value="opt.value"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                  </div>

                  <div v-if="scheduleRepeatInterval === 'week'" class="col-md-6">
                    <label class="form-label small fw-medium" for="schedule-recurring-weekday">Day</label>
                    <select
                      id="schedule-recurring-weekday"
                      v-model="scheduleRecurringDayOfWeek"
                      class="form-select form-control push-automation-field push-flow-type-select"
                    >
                      <option value="" disabled>Select day</option>
                      <option v-for="d in scheduleWeekdayOptions" :key="d.value" :value="d.value">
                        {{ d.label }}
                      </option>
                    </select>
                  </div>

                  <div v-else-if="scheduleRepeatInterval === 'month'" class="col-md-6">
                    <label class="form-label small fw-medium" for="schedule-recurring-day-month"
                      >Date</label
                    >
                    <select
                      id="schedule-recurring-day-month"
                      v-model="scheduleRecurringDayOfMonth"
                      class="form-select form-control push-automation-field push-flow-type-select"
                    >
                      <option value="" disabled>Select date</option>
                      <option v-for="d in scheduleDayOfMonthOptions" :key="d.value" :value="d.value">
                        {{ d.label }}
                      </option>
                    </select>
                  </div>

                  <div v-else-if="scheduleRepeatInterval === 'year'" class="col-md-6">
                    <label class="form-label small fw-medium" for="schedule-recurring-date-year"
                      >Date</label
                    >
                    <div class="position-relative schedule-field-icon-wrap">
                      <input
                        id="schedule-recurring-date-year"
                        v-model="scheduleRecurringDateOfYear"
                        type="date"
                        class="form-control rounded-3 push-automation-field schedule-input-date"
                      />
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label small fw-medium" for="schedule-time-recurring">Time</label>
                    <div class="position-relative schedule-field-icon-wrap">
                      <input
                        id="schedule-time-recurring"
                        v-model="scheduleTimeRecurring"
                        type="time"
                        class="form-control rounded-3 push-automation-field schedule-input-time"
                      />
                    </div>
                  </div>
                </div>
              </template>

              <template v-else-if="scheduleType === 'custom'">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label small fw-medium" for="schedule-custom-date">Date</label>
                    <div class="position-relative schedule-field-icon-wrap">
                      <input
                        id="schedule-custom-date"
                        v-model="scheduleCustomDate"
                        type="date"
                        class="form-control rounded-3 push-automation-field schedule-input-date"
                      />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small fw-medium" for="schedule-time-custom">Time</label>
                    <div class="position-relative schedule-field-icon-wrap">
                      <input
                        id="schedule-time-custom"
                        v-model="scheduleTimeCustom"
                        type="time"
                        class="form-control rounded-3 push-automation-field schedule-input-time"
                      />
                    </div>
                  </div>
                </div>
              </template>

              <template v-else-if="scheduleType === 'event'">
                <div class="mb-3">
                  <label class="form-label small fw-medium" for="schedule-event-type">Event Type</label>
                  <select
                    id="schedule-event-type"
                    v-model="scheduleEventType"
                    class="form-select form-control push-automation-field push-flow-type-select"
                  >
                    <option value="" disabled>Select event type</option>
                    <option value="match">Match</option>
                    <!--<option value="training">Training</option> -->
                    <!--<option value="fan_event">Fan Event</option>-->
                  </select>
                </div>
                <div class="row g-3 mb-3 align-items-end">
                  <div class="col-md-6">
                    <label class="form-label small fw-medium" for="schedule-event-timing">Timing</label>
                    <select
                      id="schedule-event-timing"
                      v-model="scheduleEventTiming"
                      class="form-select form-control push-automation-field push-flow-type-select"
                    >
                      <option value="" disabled>Select timing</option>
                      <option value="before_ko">Before Kick-off</option>
                      <option value="after_ko">After Kick-off</option>
                      <option value="after_shs">After Second Time Start</option>
                      <option value="after_ft">After Full Time</option>

                    </select>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label fw-medium" for="schedule-time-offset">Time Offset</label>
                    <div class="schedule-time-offset-group">
                      <input
                        id="schedule-time-offset"
                        v-model="scheduleTimeOffset"
                        type="number"
                        min="0"
                        class="form-control rounded-3 push-automation-field schedule-offset-input"
                        placeholder="0"
                      />
                      <select
                        id="schedule-time-offset-unit"
                        v-model="scheduleTimeOffsetUnit"
                        class="form-select form-control push-automation-field push-flow-type-select schedule-offset-unit"
                      >
                        <option
                          v-for="u in scheduleTimeOffsetUnitOptions"
                          :key="u.value"
                          :value="u.value"
                        >
                          {{ u.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="mb-0">
                  <label class="form-label small fw-medium" for="schedule-apply-events">Apply to Events</label>
                  <select
                    id="schedule-apply-events"
                    v-model="scheduleApplyToEvents"
                    class="form-select form-control push-automation-field push-flow-type-select"
                  >
                    <option value="" disabled>Select events</option>
                    <option value="all">All upcoming events</option>
                    <option value="home">Home matches only</option>
                    <option value="away">Away matches only</option>
                  </select>
                </div>
              </template>
            </div>
          </div>

          <div class="card push-automation-card rounded-3 mt-4">
            <div class="card-body p-4">
              <h6 class="fw-semibold mb-1">Priority Processing</h6>
              <p class="text-muted small mb-3">
                Choose how fast this notification should be processed for the full audience.
              </p>

              <div class="mb-2 border p-3 rounded-3 bg-white">
                <label class="d-block mb-0" for="priority-low">
                  <input
                    type="radio"
                    id="priority-low"
                    name="priority_processing"
                    value="low"
                    class="me-2"
                    v-model="priority"
                  />
                  <strong>Priority Processing</strong><br />
                  <span class="text-muted ms-4"
                    >Takes approximately 60 minutes to send notification to all users</span
                  >
                </label>
              </div>

              <div class="mb-0 border p-3 rounded-3 bg-white">
                <label class="d-block mb-0" for="priority-high">
                  <input
                    type="radio"
                    id="priority-high"
                    name="priority_processing"
                    value="high"
                    class="me-2"
                    v-model="priority"
                  />
                  <strong>High Priority Processing</strong><br />
                  <span class="text-muted ms-4"
                    >Takes approximately 30 minutes to send notification to all users</span
                  >
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-4 push-automation-save-sticky">
          <div class="card rounded-3 mb-4">
            <div class="card-body p-4" style="background-color: #f3f3f5">
              <h6 class="fw-semibold mb-3">Notification Preview</h6>

              <div class="preview-ios mb-3 p-3 bg-white rounded-3 border">
                <div class="d-flex align-items-start">
                  <img
                    v-if="useClubAppIconIosInPreview"
                    :src="clubAppIconIosUrl"
                    alt=""
                    class="preview-app-icon preview-app-icon-img rounded-3 flex-shrink-0 me-2"
                    @error="onClubAppIconIosError"
                  />
                  <div
                    v-else
                    class="preview-app-icon rounded-3 bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0 me-2"
                  >
                    {{ clubInitials }}
                  </div>
                  <div class="flex-grow-1 min-w-0">
                    <div class="d-flex justify-content-between align-items-baseline mb-1">
                      <span class="small fw-semibold text-truncate">{{ clubAppLabel }}</span>
                      <span class="text-muted small flex-shrink-0 ms-2">now</span>
                    </div>
                    <div class="fw-semibold small mb-1">{{ previewTitle }}</div>
                    <div class="text-muted small preview-message">{{ previewMessage }}</div>
                  </div>
                </div>
              </div>

              <div class="preview-android p-3 bg-white rounded-3 border">
                <div class="d-flex align-items-start">
                  <img
                    v-if="useClubAppIconAndroidInPreview"
                    :src="clubAppIconAndroidUrl"
                    alt=""
                    class="preview-app-icon preview-app-icon-img rounded-circle flex-shrink-0 me-2"
                    @error="onClubAppIconAndroidError"
                  />
                  <div
                    v-else
                    class="preview-app-icon fs-6 rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0 me-2"
                  >
                    {{ clubInitials }}
                  </div>
                  <div class="flex-grow-1 min-w-0">
                    <div class="d-flex align-items-baseline mb-1">
                      <div class="small fw-semibold mb-0">{{ clubAppLabel }}</div>
                      <div class="text-muted extra-small mb-1">· now</div>
                    </div>
                    <div class="fw-semibold small mb-1">{{ previewTitle }}</div>
                    <div class="text-muted small preview-message">{{ previewMessage }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div class="card rounded-3">
              <div class="card-body p-4">
                <h6 class="fw-semibold mb-3">Save</h6>
           <!-- <b-button
                  block
                  variant="dark"
                  class="w-100 mb-2 py-2"
                  :disabled="tryingToSubmit"
                  @click="saveAsDraft"
                >
                  <b-spinner v-show="tryingToSubmit" small class="me-1" />
                  Save as Draft
                </b-button>
            -->
                <b-button
                  block
                  variant="dark"
                  class="w-100 mb-3 py-2 push-automation-btn-activate"
                  :disabled="tryingToSubmit"
                  @click="saveAndActivate"
                >
                  Save &amp; Send
                </b-button>
                <div class="text-center">
                  <button
                    type="button"
                    class="btn btn-link text-muted text-decoration-none push-automation-btn-cancel w-100 rounded-2 px-3 py-2"
                    @click="closeModal"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<style scoped>
.push-automation-body {
  min-height: 320px;
}

.rich-image-preview {
  width: 92px;
  height: 92px;
  object-fit: cover;
}

/* Sticky Save column block while modal body scrolls */
.push-automation-save-sticky {
  position: sticky;
  top: 1rem;
  z-index: 5;
  align-self: flex-start;
}

.push-automation-body .push-automation-card {
  border: 1px solid #e5e5e5;
  background-color: #fff;
  box-shadow: none;
}

.preview-app-icon {
  width: 36px;
  height: 36px;
  font-size: 11px;
  font-weight: 700;
}

.preview-app-icon-img {
  object-fit: cover;
  display: block;
  background-color: #fff;
}

.preview-message {
  line-height: 1.35;
}

.extra-small {
  font-size: 0.7rem;
}

/* Shared field look: same as Type select (#f3f3f5 + subtle border) */
.push-automation-body select.push-automation-field,
.push-automation-body input.form-control.push-automation-field,
.push-automation-body textarea.form-control.push-automation-field {
  background-color: #f3f3f5 !important;
  border-color: rgba(0, 0, 0, 0.06) !important;
}

.push-automation-body select.push-automation-field,
.push-automation-body input.form-control.push-automation-field {
  min-height: 42px;
}

.push-automation-body select.push-automation-field:focus,
.push-automation-body input.form-control.push-automation-field:focus,
.push-automation-body textarea.form-control.push-automation-field:focus {
  background-color: #f3f3f5 !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
  box-shadow: none !important;
}

.form-select {
  border-radius: 10px;
}

/* Save & Activate — outline + hover fill */
.push-automation-body .btn.push-automation-btn-activate.btn-outline-dark {
  border-color: #f3f3f5 !important;
}

.push-automation-body .btn.push-automation-btn-activate.btn-outline-dark:hover:not(:disabled),
.push-automation-body .btn.push-automation-btn-activate.btn-outline-dark:focus:hover:not(:disabled) {
  background-color: #f3f3f5 !important;
  color: #212529 !important;
  border-color: #e5e5e5 !important;
}

/* Save & Send — solid black */
.push-automation-body .btn.push-automation-btn-activate.btn-dark {
  background-color: #212529 !important;
  border-color: #212529 !important;
  color: #fff !important;
}

.push-automation-body .btn.push-automation-btn-activate.btn-dark:hover:not(:disabled),
.push-automation-body .btn.push-automation-btn-activate.btn-dark:focus:hover:not(:disabled) {
  background-color: #111418 !important;
  border-color: #111418 !important;
  color: #fff !important;
}

.push-automation-body .btn.push-automation-btn-cancel {
  border: none !important;
  box-shadow: none !important;
  background-color: #fff !important;
  color: #6c757d !important;
}

/* White by default; #f3f3f5 fill only on hover */
.push-automation-body .btn.push-automation-btn-cancel:hover {
  background-color: #f3f3f5 !important;
  color: #212529 !important;
  border: none !important;
  box-shadow: none !important;
}

.push-automation-body .btn.push-automation-btn-cancel:focus,
.push-automation-body .btn.push-automation-btn-cancel:active {
  background-color: #fff !important;
  border: none !important;
  box-shadow: none !important;
}

.push-automation-body .btn.push-automation-btn-cancel:focus-visible {
  outline: 2px solid rgba(33, 37, 41, 0.25);
  outline-offset: 2px;
  background-color: #fff !important;
}

.audience-sub-label {
  letter-spacing: 0.03em;
  font-size: 0.7rem;
}

.audience-search-icon {
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 1;
}

.audience-suggest-list {
  z-index: 20;
  max-height: 220px;
  overflow-y: auto;
  border-radius: 0.375rem;
}

.audience-estimated-box {
  background-color: #f3f3f5;
}

.cursor-pointer {
  cursor: pointer;
}

/* Audience: mock — muted search when system group selected */
.audience-user-search-wrap--muted .audience-user-search-input {
  opacity: 0.75;
  cursor: not-allowed !important;
}

.audience-or-divider {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  margin: 0.25rem 0 1rem;
  color: #6c757d;
}

.audience-or-divider span {
  height: 1px;
  background: #dee2e6;
}

.audience-or-divider strong {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

/* Single active selection row: light blue fill, blue border, dot + name + × */
.audience-active-pill {
  padding: 0.65rem 0.85rem;
  border-radius: 10px;
  border: 1px solid #b6d4fe;
  background-color: #e8f2fc;
}

.audience-active-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #0d6efd;
  margin-right: 0.65rem;
}

.audience-active-name {
  color: #212529;
}

.audience-active-clear {
  border: none;
  background: transparent;
  padding: 0 0.15rem;
  margin: 0;
  line-height: 1;
  font-size: 1.35rem;
  font-weight: 400;
  color: #0d6efd;
  cursor: pointer;
  border-radius: 6px;
}

.audience-active-clear:hover {
  color: #0a58ca;
  background-color: rgba(13, 110, 253, 0.1);
}

.audience-estimated-footnote {
  line-height: 1.35;
}

/* Time offset: wide number field, compact unit dropdown */
.schedule-time-offset-group {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 5.25rem;
  align-items: stretch;
  gap: 0.5rem;
}

.schedule-time-offset-group .schedule-offset-input {
  min-width: 0;
  width: 100%;
}

.schedule-time-offset-group .schedule-offset-unit {
  width: 100%;
  min-width: 0;
  padding-left: 0.5rem;
  padding-right: 1.75rem;
  font-size: 0.8125rem;
}
</style>

<!-- dialog-class merges onto `.modal-dialog` inside b-modal (not this SFC’s scoped tree) -->
<style>
/* Wins over Bootstrap’s `.modal-xl` (1140px) when both classes apply */
.modal-dialog.modal-dialog-push-automation-wide {
  max-width: min(1320px, 98vw);
}
.card{
  box-shadow: none !important;
}
</style>
